import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.pexels.com/photos/1188553/pexels-photo-1188553.jpeg";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-ink text-paper grain">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video
          src="https://customer-assets.emergentagent.com/job_interactive-showcase-79/artifacts/ohfw523r_los-angeles-california-circa-2017-aerial-view-of-suburban-neighborhood-between-hill-SBV-347678320-HD.mp4"
          poster={HERO_IMG}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
          aria-label="Aerial view of Southern California suburban neighborhood"
        />
        <img
          src={HERO_IMG}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover -z-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />
      </motion.div>

      {/* Crosshair grid */}
      <div className="absolute inset-0 grid-pattern opacity-[0.08] pointer-events-none" />

      {/* Top edge — Southern California only */}
      <div className="hidden md:block absolute top-20 lg:top-24 inset-x-0 z-10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between text-paper/55 mono text-[10px] uppercase tracking-[0.3em]">
          <span>Southern California</span>
          <span className="hidden lg:inline">35+ years</span>
          <span>$350M+ Transactions</span>
        </div>
      </div>

      {/* Headline */}
      <motion.div style={{ opacity }} className="absolute inset-0 z-10 flex flex-col justify-end pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1400px] w-full px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-primary-glow" />
            <span className="mono text-[11px] uppercase tracking-[0.3em] text-primary-glow">Southern California</span>
          </div>

          <h1 className="font-display text-[clamp(2.75rem,8vw,8.5rem)] leading-[0.92] tracking-[-0.035em] text-paper max-w-[14ch] text-balance">
            Where the next
            <span className="block italic font-light text-paper/85">master plan</span>
            <span className="block">begins.</span>
          </h1>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <p className="md:col-span-5 text-base lg:text-lg text-paper/75 leading-relaxed max-w-prose">
              35+ years of land development across Southern California — with a current focus on entitlement and executive management for the builders shaping the region.
            </p>

            <div className="md:col-span-4 md:col-start-7 flex flex-wrap gap-3">
              <Button asChild className="h-12 rounded-none bg-paper text-ink hover:bg-primary-glow hover:text-ink mono text-[11px] uppercase tracking-[0.22em] px-6">
                <Link to="/contact-us">
                  Start a Project <ArrowUpRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12 rounded-none bg-transparent border-paper/30 text-paper hover:bg-paper hover:text-ink mono text-[11px] uppercase tracking-[0.22em] px-6">
                <Link to="/what-we-have-done">Our Work</Link>
              </Button>
            </div>

            <div className="md:col-span-3 md:col-start-10 grid grid-cols-2 gap-x-6 gap-y-4 text-paper/80">
              <div>
                <div className="font-display text-3xl text-paper">$350M+</div>
                <div className="mono text-[10px] uppercase tracking-[0.18em] text-paper/50 mt-1">Transactions</div>
              </div>
              <div>
                <div className="font-display text-3xl text-paper">2,200+</div>
                <div className="mono text-[10px] uppercase tracking-[0.18em] text-paper/50 mt-1">Acres in plans</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Scroll cue — desktop only (overlaps ticker on mobile) */}
      <button onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })} className="hidden md:flex absolute bottom-6 left-6 lg:left-10 z-10 items-center gap-3 group">
        <span className="mono text-[10px] uppercase tracking-[0.22em] text-paper/60 group-hover:text-paper transition-colors">Scroll</span>
        <span className="relative inline-flex h-8 w-px bg-paper/30 overflow-hidden">
          <span className="absolute inset-x-0 -top-4 h-4 bg-primary-glow animate-[marquee_2s_linear_infinite]" style={{ animationName: 'fade-down' }}/>
        </span>
        <ArrowDown className="h-3.5 w-3.5 text-paper/60 group-hover:text-paper transition-transform group-hover:translate-y-0.5" />
      </button>

      {/* Ticker */}
      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-paper/15 bg-ink/40 backdrop-blur-sm">
        <div className="overflow-hidden">
          <div className="flex ticker whitespace-nowrap items-stretch">
            {['rail-a', 'rail-b'].map((railKey) => (
              <div key={railKey} className="flex items-stretch shrink-0">
                {[
                  '$350M+ equity transactions',
                  '5 specific plans authored',
                  '2,200+ acres of approved framework',
                  '4,000-acre New Model Colony',
                  '35+ years across Southern California',
                ].map((label) => (
                  <span
                    key={`${railKey}-${label}`}
                    className="inline-flex items-center justify-center px-8 lg:px-12 py-3 mono text-[11px] uppercase tracking-[0.28em] text-paper/55 border-r border-paper/10 min-w-[260px] lg:min-w-[340px]"
                  >
                    {label}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
